import React, { useState, useRef, useEffect } from 'react';
import Employee from '../../types/api/Employee';
import { IconButton, Box, Chip, ToggleButton, ToggleButtonGroup, Menu, MenuItem, ListItemIcon, ListItemText, useTheme,  } from '@mui/material';
import { days } from '../../types/api/Days';
import { getWeekNumber } from '../../tools/utils';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ArchiveIcon from '@mui/icons-material/Archive';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { EmployeeFormation } from '../../types/api/EmployeeFormation';
import DeleteIcon from '@mui/icons-material/Delete';

interface EmployeesTableProps {
  handleEmployeeEdition: (employee: Employee) => void,
  handleArchiveEmployee: (employee: Employee) => void,
  handleActiveEmployee: (employee: Employee) => void,
  handleLeaveDay: (employee: Employee) => void,
  handleViewProfile: (employee: Employee) => void,
  handleDeleteEmployee: (employee: Employee) => void,
  data: Employee[],
  selectedEmployeeIds: number[],
  onSelectionChange: (selectedIds: number[]) => void,
}
function EmployeesTable(EmployeesTableProps: EmployeesTableProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeFilter, setActiveFilter] = useState<'active' | 'inactive'>('active');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Calculer les colonnes visibles en fonction de la largeur
  const getColumnVisibility = () => {
    // Largeurs approximatives: checkbox(50) + nom(200) + formation(200) + disponibilites(250) + actions(50) = 750px minimum
    const baseWidth = 1000;
    const dateEntreeWidth = 120;
    const telephoneWidth = 150;
    const heuresWidth = 120;
    const tauxHoraireWidth = 130;

    const visibility = {
      dateEntree: containerWidth >= baseWidth + dateEntreeWidth + heuresWidth + tauxHoraireWidth + telephoneWidth,
      telephone: containerWidth >= baseWidth + telephoneWidth,
    };

    // Pour les employés actifs
    if (activeFilter === 'active') {
      return {
        ...visibility,
        heures: containerWidth >= baseWidth + heuresWidth + tauxHoraireWidth + telephoneWidth,
        tauxHoraire: containerWidth >= baseWidth + tauxHoraireWidth + telephoneWidth,
        disponibilites: true,
        dateArchivage: false,
      };
    } 
    // Pour les employés archivés
    else {
      return {
        ...visibility,
        heures: false,
        tauxHoraire: false,
        disponibilites: false,
        dateArchivage: true,
      };
    }
  };

  const columnVisibility = getColumnVisibility();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, employee: Employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleEdit = () => {
    if (selectedEmployee) {
      EmployeesTableProps.handleEmployeeEdition({...selectedEmployee});
    }
    handleMenuClose();
  };

  const handleLeaveDay = () => {
    if (selectedEmployee) {
      EmployeesTableProps.handleLeaveDay({...selectedEmployee});
    }
    handleMenuClose();
  };

  const handleArchive = () => {
    if (selectedEmployee) {
      const updatedEmployee = { ...selectedEmployee, isActive: false };
      EmployeesTableProps.handleArchiveEmployee(updatedEmployee);
    }
    handleMenuClose();
  };

  const handleActive = () => {
    if (selectedEmployee) {
      const updatedEmployee = { ...selectedEmployee, isActive: true };
      EmployeesTableProps.handleActiveEmployee(updatedEmployee);
    }
    handleMenuClose();
  };

  const handleViewProfile = () => {
    if (selectedEmployee) {
      EmployeesTableProps.handleViewProfile({...selectedEmployee});
    }
    handleMenuClose();
  }

  const handleDelete = () => {
    if (selectedEmployee) {
      EmployeesTableProps.handleDeleteEmployee({...selectedEmployee});
    }
    handleMenuClose();
  }

  function renderAvailabilityIcon(isAvailableMidi: boolean, isAvailableSoir: boolean) {
    if (isAvailableMidi && isAvailableSoir) {
      return <Box sx={{
        width:20,
        height:20,
        borderRadius:"50%",
        backgroundColor:theme.palette.success.light,
        border:"2px solid " + theme.palette.success.light
        }}/>;
    } else if (isAvailableMidi && !isAvailableSoir) {
      return <Box sx={{
      width:20,
      height:20,
      borderRadius:"50%",
      background:"linear-gradient(90deg, " + theme.palette.success.light + " 50%, transparent 50%)",
      border:"2px solid " + theme.palette.success.light
    }}/>;
    } else if (!isAvailableMidi && isAvailableSoir) {
      return <Box sx={{
      width:20,
      height:20,
      borderRadius:"50%",
      background:"linear-gradient(90deg, transparent 50%, " + theme.palette.success.light + " 50%)",
      border:"2px solid " + theme.palette.success.light
    }}/>;
    }else {
      return <Box sx={{
        width:20,
        height:20,
        borderRadius:"50%",
        backgroundColor:theme.palette.error.main,
        border:"2px solid " + theme.palette.error.main
        }}/>;
    }
  }


  const columns: GridColDef[] = [
    { 
      field: 'nom', 
      headerName: 'Nom complet', 
      flex: 1,
      maxWidth: 600,
      minWidth: 150,
      renderCell: (params: any) => {
        const employee = params.row;
        return (
          <div style={{color: "text.contrastText", display: 'flex', alignItems: 'center', height: '100%'}}>
            {' ' + (employee.lastName ? employee.lastName.toUpperCase() : '') + ' ' + (employee.firstName || '')}
          </div>
        );
      }
    },
    { 
      field: 'formation', 
      headerName: 'Formation', 
      flex: 1,
      minWidth: 180,
      renderCell: (params: any) => {
        const employee = params.row;
        return employee.formations.length > 0 ? (
          <div className='w-full text-left py-2 flex gap-2 flex-wrap'>
            {employee.formations.map((formation: EmployeeFormation, index: number) => (
              <Chip key={index} label={formation.formation.label} size="small" />
            ))}
          </div>
        ) : (
          <div className='text-sm italic' style={{color: theme.palette.text.secondary}}>Aucune formation</div>
        );
      }
    },
    { 
      field: 'disponibilites', 
      headerName: 'Disponibilités', 
      width: 250,
      minWidth: 200,
      renderCell: (params: any) => {
        const employee = params.row;
        // Ne pas afficher pour les employés archivés
        if (!employee.isActive) {
          return null;
        }
        return (
          <div style={{color: "text.contrastText", display: 'flex', gap: '4px', padding: '4px 0', whiteSpace: 'nowrap', alignItems: 'center', height: '100%'}}>
            {employee.hasWeeklyAvailabilities ? 
              // Is in weekly availabilities, check for latest weekly availability
              Object.keys(days).map((dayKey) => {
                const availabilities = employee.WeeklyAvailabilities.find((item: any) => {
                  return item.year === new Date().getFullYear() && item.weekNumber === getWeekNumber(new Date())
                });
                // Si pas de disponibilité pour cette semaine, utiliser les disponibilités par défaut
                let isAvailableMidi: boolean;
                let isAvailableSoir: boolean;
                
                if(!availabilities) {
                  isAvailableMidi = employee[dayKey + 'Noon' as keyof Employee] as boolean;
                  isAvailableSoir = employee[dayKey + 'Evening' as keyof Employee] as boolean;
                } else {
                  isAvailableMidi = availabilities[dayKey + 'Noon' as keyof typeof availabilities] as boolean;
                  isAvailableSoir = availabilities[dayKey + 'Evening' as keyof typeof availabilities] as boolean;
                }
                
                return (
                  <div key={dayKey} style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <div className='text-xs'>{days[dayKey as keyof typeof days].slice(0, 3)}</div>
                    {renderAvailabilityIcon(isAvailableMidi, isAvailableSoir)}
                  </div>
                );
              })
              :
              // Is not in weekly availabilities, use booleans
              Object.keys(days).map((dayKey) => {
                const isAvailableMidi = employee[dayKey + 'Noon' as keyof Employee] as boolean;
                const isAvailableSoir = employee[dayKey + 'Evening' as keyof Employee] as boolean;  
                return (
                  <div key={dayKey} style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <div className='text-xs'>{days[dayKey as keyof typeof days].slice(0, 3)}</div>
                    {renderAvailabilityIcon(isAvailableMidi, isAvailableSoir)}
                  </div>
                );
              })
            }
          </div>
        );
      }
    },
    {
        field: 'telephone', 
        headerName: 'Telephone', 
        flex: 0,
        width: 150,
        minWidth: 150,
        renderCell: (params: any) => { 
          const employee = params.row;
          return (
            <Box sx={{color: "secondary.contrastText", display: 'flex', alignItems: 'center', height: '100%'}}>
              {employee.phone || '-'}
            </Box>
          );
        }
    },
    {
      field: 'tauxHoraire',
      headerName: 'Taux Horaire',
      flex: 0,
      width: 130,
      minWidth: 130,
      renderCell: (params: any) => {
        const employee = params.row;
        // Ne pas afficher pour les employés archivés
        if (!employee.isActive) {
          return null;
        }
        return (
          <Box sx={{color: "secondary.contrastText", display: 'flex', alignItems: 'center', height: '100%'}}>
            {employee.hourlyRate !== undefined && employee.hourlyRate !== null ? employee.hourlyRate.toFixed(2) + ' €' : '-'}
          </Box>
        );
      }
    },
    {
      field: 'heures',
      headerName: 'Heures',
      flex: 0,
      width: 120,
      minWidth: 120,
      renderCell: (params: any) => {
        const employee = params.row;
        // Ne pas afficher pour les employés archivés
        if (!employee.isActive) {
          return null;
        }
        return (
          <Box sx={{
            color: "secondary.contrastText",
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            height: '100%',
            gap: '4px'
          }}>
            <div style={{lineHeight: '1.2'}}>{employee.hoursPerMonth}H/mois</div>
            <div style={{lineHeight: '1.2'}}>{employee.hoursPerWeek}H/sem</div>
          </Box>
        );
      }
    },
    {
      field: 'dateEntree',
      headerName: "Date d'entrée",
      flex: 0,
      width: 130,
      minWidth: 130,
      renderCell: (params: any) => {
        const employee = params.row;
        const entryDate = new Date(employee.entryDate);
        return (
          <Box sx={{color: "secondary.contrastText", display: 'flex', alignItems: 'center', height: '100%'}}>
            {entryDate.toLocaleDateString()}
          </Box>
        );
      }
    },
    {
      field: 'dateArchivage',
      headerName: "Date d'archivage",
      flex: 0,
      width: 150,
      minWidth: 150,
      renderCell: (params: any) => {
        const employee = params.row;
        // Afficher uniquement pour les employés archivés
        if (!employee.isActive && employee.contrats && employee.contrats.length > 0) {
          // Trouver le contrat avec la date de fin la plus récente
          const latestContrat = employee.contrats
            .filter((c: any) => c.endDate)
            .sort((a: any, b: any) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())[0];
          
          if (latestContrat && latestContrat.endDate) {
            const archiveDate = new Date(latestContrat.endDate);
            return (
              <Box sx={{color: "secondary.contrastText", display: 'flex', alignItems: 'center', height: '100%'}}>
                {archiveDate.toLocaleDateString()}
              </Box>
            );
          }
        }
        return null;
      }
    },
    { 
      field: 'actions', 
      headerName: '', 
      width: 50,
      renderCell: (params) => {
        const employee = params.row;
        return (
          <div className='flex gap-2'>             
            <IconButton 
              size='small' 
              onClick={(e) => handleMenuOpen(e, employee)}
            >
              <MoreVertIcon />
            </IconButton>
          </div>
        );
      }
    }
  ];

  const filteredData = EmployeesTableProps.data.filter((employee) => {
    if (activeFilter === 'active') {
      return employee.isActive;
    } else {
      return !employee.isActive;
    }
  });

  const rows = filteredData.map((employee) => ({
    ...employee,
    id: employee.id
  }));

  const handleFilterChange = (event: React.MouseEvent<HTMLElement>, newFilter: 'active' | 'inactive') => {
    if (newFilter !== null) {
      setActiveFilter(newFilter);
    }
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>

      <ToggleButtonGroup
        value={activeFilter}
        exclusive
        onChange={handleFilterChange}
        aria-label="statut employé"
        size="small"
      >
        <ToggleButton value="active" aria-label="actifs">
          Actifs ({EmployeesTableProps.data.filter(employee => employee.isActive).length})
        </ToggleButton>
        <ToggleButton value="inactive" aria-label="inactifs">
          Archivés ({EmployeesTableProps.data.filter(employee => !employee.isActive).length})
        </ToggleButton>
      </ToggleButtonGroup>
      
      <DataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSorting
        disableColumnResize
        getRowHeight={() => 'auto'}
        hideFooterSelectedRowCount
        sx={{ height: 'auto' }}
        getRowId={(row) => row.id}
        columnVisibilityModel={columnVisibility}
        rowSelectionModel={EmployeesTableProps.selectedEmployeeIds}
        onRowSelectionModelChange={(newSelection) => {
          EmployeesTableProps.onSelectionChange(newSelection as number[]);
        }}
        onPaginationModelChange={(model) => {
          setPageSize(model.pageSize);
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        localeText={{
        noRowsLabel: 'Aucun employé',
        MuiTablePagination: {
          labelRowsPerPage: 'Lignes par page :'
        }
      }}
        pageSizeOptions={[10, 25, 50]}

      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedEmployee && (
          <>
            {selectedEmployee.isActive ? (
              <>
                <MenuItem onClick={handleEdit}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Modifier l'employé</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLeaveDay}>
                  <ListItemIcon>
                    <AssignmentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Jour de congé</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleArchive}>
                  <ListItemIcon>
                    <ArchiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Archiver l'employé</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleViewProfile}>
                  <ListItemIcon >
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Voir le profil de l'employé</ListItemText>
                </MenuItem>
              </>
            ) : (
              <>
              <MenuItem onClick={handleActive}>
                <ListItemIcon>
                  <ArchiveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Désarchiver l'employé</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDelete}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Supprimer l'employé</ListItemText>
              </MenuItem>
              </>
            )}
          </>
        )}
        
      </Menu>
      
    </div>
  );
}

export default EmployeesTable;